import _ from "lodash";
import client from "lib/apolloClient/client";
import {
  resourceNodeFragment,
  resourcePlannerFragment,
  staffDetailUnitPlanFeedFragment,
  unitPlanResourceLibraryFragment,
  courseDetailUnitPlanFeedFragment,
  unitPlanFeedFragment,
  organizationUnitPlanFeedFragment,
  unitTypeFragment,
  pypElementFragment,
  plannerElementNodeFragment,
} from "./UnitPlanFragments";
import {
  getUnitPlansQuery,
  getAssessmentsQuery,
  getPlannerFieldResponsesQuery,
  getUnitPlanResourceLibraryQuery,
  getPlannerResourcesOfNodeQuery,
  getNodeUnitPlanFeedQuery,
  getPlannerOrganizationResourcesOfNodeQuery,
  getNodeUnitPlanEvidenceFeedQuery,
  getFlatUnitPlansQuery,
  getMypRepoOrganizationDetailsQuery,
  getCentralRepoOrgDetailsQuery,
  getFlatUnitPlanFeedQuery,
  getFlatUnitPlanTitleQuery,
  getStaffCoursesBasicDetailsQuery,
  getSubjectGroupsQuery,
  getTemplateFieldsQuery,
  getTemplateBasicDetailsOfGradeQuery,
  getPlannerElementSetsOfFieldsQuery,
  getFlatUnitPlanFeedV2Query,
  getAllCollaboratorsQuery,
  getCustomOrderingUnitPlansQuery,
  getGradeDetailsQuery,
  getPlannerElementNodeSetIdsOfCurriculumQuery,
  getPlannerElementNodeSetsFromIdsQuery,
} from "./UnitPlanQuery";
import {
  getCustomOrderingUnitPlansinLearningCourseQuery,
  getLearningCourseUnitPlanFeedQuery,
} from "./LearningCourseFlow/UnitPlanLearningCourseQuery";
import { learningCourseUnitPlanFeedFragment } from "./LearningCourseFlow/UnitPlanLearningCourseFragment";

const fragmentNameMapping = {
  UNIT_PLAN: {
    typeName: "UnitPlan",
    fragment: "unitPlan",
    fragmentName: "unitPlanNodeItem",
  },
  ASSESSMENT: {
    typeName: "Assessment",
    fragment: "assessment",
    fragmentName: "assessmentNodeItem",
  },
  LEARNING_ENGAGEMENT: {
    typeName: "LearningEngagement",
    fragment: "le",
    fragmentName: "leNodeItem",
  },
  ATL: {
    typeName: "PlannerATL",
    fragment: "atl",
    fragmentName: "atlItem",
  },
  CONCEPT: {
    typeName: "PlannerConcept",
    fragment: "concept",
    fragmentName: "conceptItem",
  },
  LEARNER_PROFILE: {
    typeName: "PlannerLearnerProfile",
    fragment: "lp",
    fragmentName: "lpItem",
  },
  THEME: {
    typeName: "PlannerTheme",
    fragment: "theme",
    fragmentName: "themeItem",
  },
  DP_LEARNER_PROFILE: {
    typeName: "PlannerElementNode",
    fragment: "nonPypPlannerElementNode",
    fragmentName: "nonPypPlannerElementNodeItem",
  },
};

const unitPlanFeedParentFragmentMapping = {
  STAFF: {
    typeName: "Staff",
    fragment: staffDetailUnitPlanFeedFragment.staffDetailUnitPlanFeed,
    fragmentName: "staffDetailUnitPlanFeedItem",
  },
  COURSE: {
    typeName: "Course",
    fragment: courseDetailUnitPlanFeedFragment.courseUnitPlanFeed,
    fragmentName: "courseFlatUnitPlanFeedItem",
  },
};

export const getGradesDetailsFromCache = grades => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getGradeDetailsQuery,
      variables: {
        ids: grades,
      },
    });
  } catch (e) {
    console.error(e);
  }

  if (queryData != null) return _.get(queryData, "multiNode", []);
  else return [];
};

export const uniquelyCombineGradeDetails = gradeList => {
  return _.reduce(
    gradeList,
    (result, grade) => {
      result["id"] = [...(result["id"] || []), grade.id];
      result["name"] = [...(result["name"] || []), grade.name];
      result["subjects"] = _.unionBy(
        result["subjects"],
        grade["subjects"],
        "id"
      );
      result["atlCategories"] = _.unionBy(
        result["atlCategories"],
        grade["atlCategories"],
        "id"
      );
      result["plannerTemplates"] = _.unionBy(
        result["plannerTemplates"],
        grade["plannerTemplates"],
        "id"
      );

      return result;
    },
    {}
  );
};

export const getPlannerElementFromCache = ({
  type,
  id,
  isPypElement = true,
}) => {
  let queryData = {};
  try {
    const typeName = fragmentNameMapping[type].typeName;
    const fragment = fragmentNameMapping[type].fragment;
    const fragmentName = fragmentNameMapping[type].fragmentName;

    queryData = client.readFragment({
      id: `${typeName}:${id}`,
      fragment: isPypElement
        ? pypElementFragment[fragment]
        : plannerElementNodeFragment[fragment],
      fragmentName: fragmentName,
    });
  } catch (e) {
    console.error(e);
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getUniquelyCombinedGradesDetails = grades => {
  const gradeList = getGradesDetailsFromCache(grades);
  return uniquelyCombineGradeDetails(gradeList);
};

export const getSubjectGroupsFromCache = ({ organizationId, filters }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getSubjectGroupsQuery,
      variables: {
        organizationId,
        filters,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getPlannerResourcesOfNodeFromCache = ({ nodeId, type }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getPlannerResourcesOfNodeQuery,
      variables: {
        id: nodeId,
        type,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getPlannerOrganizationResourcesOfNodeFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getPlannerOrganizationResourcesOfNodeQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const writePlannerResourcesOfNodeInCache = ({ nodeId, type, data }) => {
  let queryData = {};
  try {
    const typeName = fragmentNameMapping[type].typeName;
    const fragment = fragmentNameMapping[type].fragment;
    const fragmentName = fragmentNameMapping[type].fragmentName;
    queryData = client.writeFragment({
      id: `${typeName}:${nodeId}`,
      fragment: resourceNodeFragment[fragment],
      fragmentName: fragmentName,
      data,
    });
  } catch (e) {
    console.error(e);
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const writeResourceInCache = ({ id, data }) => {
  let queryData = {};

  try {
    queryData = client.writeFragment({
      id: `PlannerResource:${id}`,
      fragment: resourcePlannerFragment.resource,
      fragmentName: "resourcePlannerItem",
      data: data,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

export const getUnitPlanFeedFromCache = ({
  id,
  filters,
  groupBy,
  type,
  showCollaborators = false,
  centralRepoInput,
  collaboratorsFilter,
}) => {
  let queryData = {};
  const {
    typeName,
    fragmentName,
    fragment,
  } = unitPlanFeedParentFragmentMapping[type];
  try {
    queryData = client.readFragment({
      id: `${typeName}:${id}`,
      fragment,
      fragmentName,
      variables: {
        filters,
        groupBy,
        showCollaborators,
        centralRepoInput,
        collaboratorsFilter,
      },
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

// TODO: make new query and do the cleanup
export const getFlatUnitPlanFeedFromCache = ({
  id,
  first,
  orderBy,
  orderByDirection,
  filters,
  showCollaborators = false,
  centralRepoInput,
  exceptionalCollaboratorsFilter,
  collaboratorsFilter,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getFlatUnitPlanFeedQuery,
      variables: {
        id,
        filters,
        showCollaborators,
        first,
        orderBy,
        orderByDirection,
        centralRepoInput,
        exceptionalCollaboratorsFilter,
        collaboratorsFilter,
      },
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

export const getFlatUnitPlanTitleFromCache = ({
  id,
  first,
  orderBy,
  orderByDirection,
  filters,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getFlatUnitPlanTitleQuery,
      variables: {
        id,
        filters,
        first,
        orderBy,
        orderByDirection,
      },
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

export const writeFlatUnitPlansInCache = ({
  id,
  uids,
  orderByDirection,
  orderBy,
  filters,
  first,
  data,
  exceptionalCollaboratorsFilter,
}) => {
  try {
    client.writeQuery({
      query: getFlatUnitPlansQuery,
      variables: {
        id,
        uids,
        orderByDirection,
        orderBy,
        filters,
        first,
        exceptionalCollaboratorsFilter,
      },
      data,
    });
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const getUnitPlanResourceLibraryFromCache = ({ id, filters }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getUnitPlanResourceLibraryQuery,
      variables: {
        id,
        filters,
      },
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const writeUnitPlanResourceLibraryInCache = ({ id, data, filters }) => {
  let queryData = {};
  try {
    queryData = client.writeFragment({
      id: `UnitPlan:${id}`,
      fragment: unitPlanResourceLibraryFragment.unitPlanResourceLibrary,
      fragmentName: "unitPlanResourceLibraryItem",
      variables: { filters },
      data,
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return queryData;
  else return {};
};

// export const getUnitPlanFeedFromCache = staffId => {
//   client.readFragment({
//     id: `Staff:${staffId}`,
//     fragment: staffDetailUnitPlanFeedFragment.staffDetailUnitPlanFeed,
//     fragmentName: "staffDetailUnitPlanFeedItem",
//   });
// };

// export const writeStaffDetailUnitPlanFeedFragment = ({ staffId, data }) => {
//   client.writeFragment({
//     id: `Staff:${staffId}`,
//     fragment: staffDetailUnitPlanFeedFragment.staffDetailUnitPlanFeed,
//     fragmentName: "staffDetailUnitPlanFeedItem",
//     data: data
//   });
// };
export const getFlatUnitPlanV2FromCache = ({ id, queryVariables }) => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `UnitPlan:${id}`,
      fragment: unitPlanFeedFragment.unitPlanFeedV2,
      fragmentName: "unitPlanItem",
      variables: { ...queryVariables },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getFeedUnitPlanItemFromCache = ({
  id,
  showCollaborators = true,
  centralRepoInput,
  collaboratorsFilter,
}) => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `UnitPlan:${id}`,
      fragment: unitPlanFeedFragment.unitPlanFeed,
      fragmentName: "unitPlanItem",
      variables: {
        id,
        showCollaborators,
        centralRepoInput,
        collaboratorsFilter,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const writeFeedUnitPlanItemToCache = ({
  id,
  data,
  showCollaborators = true,
  centralRepoInput,
  collaboratorsFilter,
}) => {
  try {
    client.writeFragment({
      id: `UnitPlan:${id}`,
      fragment: unitPlanFeedFragment.unitPlanFeed,
      fragmentName: "unitPlanItem",
      variables: {
        id,
        showCollaborators,
        centralRepoInput,
        collaboratorsFilter,
      },
      data,
    });
  } catch (e) {
    console.error(e);
  }
};

export const getNodeUnitPlanFeedFromCache = ({
  id,
  type,
  filters,
  first,
  showCollaborators,
  centralRepoInput,
  collaboratorsFilter,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getNodeUnitPlanFeedQuery,
      variables: {
        id,
        type,
        filters,
        first,
        showCollaborators,
        centralRepoInput,
        collaboratorsFilter,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getUnitPlansFromCache = ({
  ids,
  uids,
  filters,
  pypUnitInsightFilters,
  fetchUnitInsights = false,
  fetchPYPUnitInsights = false,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getUnitPlansQuery,
      variables: {
        ids,
        uids,
        filters,
        pypUnitInsightFilters,
        fetchUnitInsights,
        fetchPYPUnitInsights,
      },
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "multiNode", []);
  else return {};
};

export const getAssessmentsFromCache = ({ ids }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getAssessmentsQuery,
      variables: {
        ids,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "multiNode", []);
  else return {};
};

export const getPlannerFieldResponsesFromCache = ({
  id,
  type,
  uids,
  responseFilter,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getPlannerFieldResponsesQuery,
      variables: {
        id,
        type,
        uids,
        responseFilter,
      },
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getNodeUnitPlanEvidenceFeedFromCache = ({
  id,
  filters,
  type,
  first,
}) => {
  let queryData = {};

  try {
    queryData = client.readQuery({
      query: getNodeUnitPlanEvidenceFeedQuery,
      variables: {
        id,
        type,
        filters,
        first,
      },
    });
  } catch (e) {
    return {};
  }

  return _.get(queryData, "node", {});
};

export const getFlatUnitPlansFromCache = ({
  id,
  uids,
  orderByDirection,
  orderBy,
  first,
  filters,
  exceptionalCollaboratorsFilter,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getFlatUnitPlansQuery,
      variables: {
        id,
        uids,
        orderByDirection,
        orderBy,
        filters,
        first,
        exceptionalCollaboratorsFilter,
      },
    });
  } catch (e) {
    return {};
  }

  if (queryData != null) return _.get(queryData, "node", {});
  else return {};
};

export const getMypRepoOrganizationDetailsFromCache = params => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getMypRepoOrganizationDetailsQuery,
      variables: params,
    });
  } catch (e) {
    return {};
  }
  if (queryData != null) return queryData;
  else return {};
};

export const getCentralRepoOrgDetailsFromCache = params => {
  try {
    const queryData = client.readQuery({
      query: getCentralRepoOrgDetailsQuery,
      variables: params,
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getStaffCoursesBasicDetailsQueryFromCache = ({
  id,
  type,
  filters,
  first,
  after,
}) => {
  try {
    const queryData = client.readQuery({
      query: getStaffCoursesBasicDetailsQuery,
      variables: {
        id,
        type,
        filters,
        first,
        after,
      },
    });
    return _.get(queryData, "node", {});
  } catch (e) {
    return {};
  }
};

export const getTemplateFieldsFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getTemplateFieldsQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return _.get(queryData, "multiNode", {});
};

export const getTemplateBasicDetailsOfGradeFromCache = ({ grades }) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getTemplateBasicDetailsOfGradeQuery,
      variables: {
        ids: grades,
      },
    });
  } catch (e) {
    return {};
  }
  return _.get(queryData, "multiNode", []);
};

export const getPlannerElementSetsOfFieldFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getPlannerElementSetsOfFieldsQuery,
      variables,
    });
    return _.get(queryData, "multiNode", []);
  } catch (err) {
    return [];
  }
};

export const getPlannerElementNodeSetIdsOfCurriculumFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getPlannerElementNodeSetIdsOfCurriculumQuery,
      variables,
    });
  } catch (e) {
    console.error(e);
  }

  if (queryData != null) {
    return _.map(
      _.get(queryData, "node.plannerElementNodeSets.edges", []),
      "node"
    );
  } else {
    return [];
  }
};

export const getPlannerElementNodeSetsFromIdsFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getPlannerElementNodeSetsFromIdsQuery,
      variables,
    });
  } catch (e) {
    console.error(e);
  }

  if (queryData != null) {
    return _.get(queryData, "multiNode", []);
  } else {
    return [];
  }
};

export const getOrganizationUnitPlanFeedV2FromCache = ({
  id,
  filters,
  showCollaborators = true,
  centralRepoInput,
  collaboratorsFilter,
  exceptionalCollaboratorsFilter,
}) => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `Organization:${id}`,
      fragment: organizationUnitPlanFeedFragment.organizationUnitPlanFeedV2,
      fragmentName: "organizationUnitPlanFeedItem",
      variables: {
        filters,
        showCollaborators,
        centralRepoInput,
        collaboratorsFilter,
        exceptionalCollaboratorsFilter,
      },
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const writeOrganizationUnitPlanFeedV2InCache = ({
  id,
  data,
  filters,
  showCollaborators = true,
  centralRepoInput,
  collaboratorsFilter,
  exceptionalCollaboratorsFilter,
}) => {
  try {
    client.writeFragment({
      id: `Organization:${id}`,
      fragment: organizationUnitPlanFeedFragment.organizationUnitPlanFeedV2,
      fragmentName: "organizationUnitPlanFeedItem",
      variables: {
        filters,
        showCollaborators,
        centralRepoInput,
        collaboratorsFilter,
        exceptionalCollaboratorsFilter,
      },
      data,
    });
  } catch (e) {}
};

export const getLearningCourseUnitPlanFeedFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getLearningCourseUnitPlanFeedQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const getFlatFeedUnitPlansFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getFlatUnitPlanFeedV2Query,
      variables,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const writeUnitPlanV2Fragment = ({
  unitPlanId,
  data,
  queryVariables,
}) => {
  try {
    client.writeFragment({
      id: `UnitPlan:${unitPlanId}`,
      fragment: unitPlanFeedFragment.unitPlanFeedV2,
      fragmentName: "unitPlanItem",
      data: data,
      variables: { ...queryVariables },
    });
  } catch (e) {}
};

export const writeLearningCourseUnitPlanFragment = ({
  unitPlanId,
  data,
  queryVariables,
}) => {
  try {
    client.writeFragment({
      id: `UnitPlan:${unitPlanId}`,
      fragment: learningCourseUnitPlanFeedFragment.learningCourseUnitPlanFeed,
      fragmentName: "learningCourseUnitPlanFeed",
      data: data,
      variables: { ...queryVariables },
    });
  } catch (e) {}
};

export const getCustomOrderedUnitPlansFromCache = variables => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getCustomOrderingUnitPlansQuery,
      variables,
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const getCustomOrderingUnitPlansinLearningCouseFromCache = variables => {
  try {
    const queryData = client.readQuery({
      query: getCustomOrderingUnitPlansinLearningCourseQuery,
      variables,
    });
    return queryData || {};
  } catch {
    return {};
  }
};

export const writeCustomOrderedUnitPlansinLearningCourseInCache = ({
  id,
  first,
  orderBy,
  orderByDirection,
  data,
}) => {
  try {
    client.writeQuery({
      query: getCustomOrderingUnitPlansinLearningCourseQuery,
      variables: {
        id,
        first,
        orderBy,
        orderByDirection,
      },
      data,
    });
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const writeCustomOrderedUnitPlansInCache = ({
  id,
  first,
  orderBy,
  orderByDirection,
  data,
}) => {
  try {
    client.writeQuery({
      query: getCustomOrderingUnitPlansQuery,
      variables: {
        id,
        first,
        orderBy,
        orderByDirection,
      },
      data,
    });
  } catch (e) {
    console.error(e);
    return {};
  }
};

export const getAllCollaboratorsFromCache = ({
  id,
  collaboratorsFilter,
  first,
}) => {
  let queryData = {};
  try {
    queryData = client.readQuery({
      query: getAllCollaboratorsQuery,
      variables: {
        id,
        collaboratorsFilter,
        first,
      },
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};

export const getUnitTypeFromCache = type_id => {
  let queryData = {};
  try {
    queryData = client.readFragment({
      id: `UnitType:${type_id}`,
      fragment: unitTypeFragment.unitType,
      fragmentName: "unitTypeItem",
    });
  } catch (e) {
    return {};
  }
  return queryData ? queryData : {};
};
